/**
 * The MIME type the web client declares for an uploaded file (WP-IMPORT-1).
 *
 * POST /v1/documents/upload/init refuses a MIME type that is not allowed, and an
 * extension that is not one of those allowed for the MIME type
 * (services/core/app/schemas/document.py ALLOWED_MIME_TYPES). Browsers report
 * types that do not match that list for common files: on Windows a .csv can be
 * reported as application/vnd.ms-excel when Excel is installed, and .qif/.qfx are
 * often reported as '' or a vendor type. So a known extension decides; the
 * browser's type is used only for files whose extension is not in the map.
 *
 * Keep this map equal to the server's ALLOWED_MIME_TYPES (inverted).
 */
export const UPLOAD_MIME_TYPES_BY_EXTENSION: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  csv: 'text/csv',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ofx: 'application/x-ofx',
  qfx: 'application/x-qfx',
  qif: 'application/x-qif',
  json: 'application/json',
  zip: 'application/zip',
};

/** Lower-case extension of a filename without the dot; '' if it has none. */
export function getFileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

/** The MIME type to send to upload/init for this file. */
export function getUploadMimeType(file: Pick<File, 'name' | 'type'>): string {
  return (
    UPLOAD_MIME_TYPES_BY_EXTENSION[getFileExtension(file.name)] ??
    (file.type || 'application/octet-stream')
  );
}
